import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Ban, AlertTriangle, Info, CheckCircle, AlertOctagon } from "lucide-react";
import { RISKS } from "@/data/staticData";

const iconMap = { Ban, AlertTriangle, Info, CheckCircle };

export default function RiskPage() {
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="risk-page">
      <div className="mb-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <AlertOctagon className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Classification des risques AI Act</h1>
        </div>
        <p className="text-[var(--ia-text-muted)]">Comprendre les 4 niveaux de risque définis par le règlement européen sur l'IA</p>
      </div>

      <div className="space-y-4">
        {RISKS.map((risk, i) => {
          const Icon = iconMap[risk.icon] || AlertTriangle;
          return (
            <Card key={risk.id} className="border-[var(--ia-border)] animate-fade-in overflow-hidden" style={{ animationDelay: `${i * 80}ms` }} data-testid={`risk-card-${risk.id}`}>
              <Accordion type="single" collapsible>
                <AccordionItem value={risk.id} className="border-none">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline [&[data-state=open]]:bg-slate-50">
                    <div className="flex items-center gap-4 text-left">
                      <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: risk.bgColor }}>
                        <Icon className="h-5 w-5" style={{ color: risk.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[var(--ia-text)]">Risque {risk.level}</h3>
                          <Badge variant="outline" className="text-[10px]" style={{ color: risk.color, borderColor: risk.color }}>{risk.level}</Badge>
                        </div>
                        <p className="text-sm text-[var(--ia-text-muted)] mt-0.5">{risk.description}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                      <div>
                        <h4 className="text-sm font-semibold mb-2 text-[var(--ia-text)]">Règles applicables</h4>
                        <ul className="space-y-1.5">
                          {risk.rules.map((r, j) => (
                            <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: risk.color }} />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold mb-2 text-red-600">À éviter</h4>
                        <ul className="space-y-1.5">
                          {risk.avoid.map((a, j) => (
                            <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                              {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold mb-2 text-amber-600">Précautions</h4>
                        <ul className="space-y-1.5">
                          {risk.precautions.map((p, j) => (
                            <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 mt-1.5" />
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold mb-2 text-[var(--ia-blue)]">Exemples concrets</h4>
                        <ul className="space-y-1.5">
                          {risk.examples.map((ex, j) => (
                            <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0 mt-1.5" />
                              {ex}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
